// Shared accessibility helpers for the end-user facing components.
//
// Every component here uses shadow DOM, so utility classes can't be inherited
// from a global stylesheet — `a11yStyles` has to be mixed into each component's
// own `static get styles()` array instead.
import { css, html } from 'lit-element';
import { t, getLocale } from './i18n.js';

// Mix into a component's styles: `static get styles() { return [a11yStyles, css`...`]; }`
export const a11yStyles = css`
    /* Visually hidden but still read by screen readers (WCAG 1.3.1 / 4.1.2). */
    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    /* WCAG 2.4.7 Focus Visible — one consistent, high-contrast focus ring.
       Uses a dark ring plus a white outer ring so it stays visible on both the
       light content areas and the teal/orange chrome. */
    :where(a, button, [tabindex]):focus-visible,
    :host(:focus-visible) {
        outline: 3px solid #1a1a1a;
        outline-offset: 2px;
        box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.9);
        border-radius: 2px;
    }

    /* Buttons that need to look exactly like the text/graphic they replaced.
       Semantics (keyboard focus, Enter/Space, role) come for free; appearance
       is inherited from the surrounding layout. */
    .unstyled-button {
        display: inline;
        background: none;
        border: 0;
        margin: 0;
        padding: 0;
        font: inherit;
        color: inherit;
        text-align: inherit;
        cursor: pointer;
    }
    .unstyled-button::-moz-focus-inner {
        border: 0;
    }

    /* WCAG 2.3.3 / 2.2.2 — respect the OS "reduce motion" setting. */
    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;

// The collapse/expand chevron used by every report section. Rendering it as a
// real <button> gives keyboard operability (2.1.1) and a name (4.1.2); the SVG
// itself is decorative because the button already carries the label.
export const disclosureButton = function(expanded, label, controlsId, onToggle) {
    return html`
        <button
            class="unstyled-button disclosure"
            type="button"
            aria-expanded=${expanded ? 'true' : 'false'}
            aria-controls=${controlsId}
            @click=${onToggle}>
            <span class="sr-only">${t(expanded ? 'a11y.hide' : 'a11y.show', { label: String(label).toLocaleLowerCase(getLocale()) })}</span>
            ${expanded ? html`
                <svg aria-hidden="true" focusable="false" height="1em" viewBox="0 0 20 10"><polygon fill="currentColor" stroke-width="0" points="0,10 20,10 10,0" /></svg>
            `: html`
                <svg aria-hidden="true" focusable="false" height="1em" viewBox="0 0 20 10"><polygon fill="currentColor" stroke-width="0" points="0,0 20,0 10,10" /></svg>
            `}
        </button>
    `;
};

// Counter used to mint unique ids for aria-controls / aria-labelledby pairs.
let _idSeq = 0;
export const uniqueId = function(prefix) {
    _idSeq += 1;
    return `${prefix}-${_idSeq}`;
};

// A single polite live region shared by the whole app, appended to <body> so it
// is outside every shadow root and survives re-renders. Used to announce SPA
// view changes and async results that have no visible focus change (WCAG 4.1.3).
let _liveRegion;
export const announce = function(message) {
    if (typeof document === 'undefined' || !message) return;
    if (!_liveRegion) {
        _liveRegion = document.createElement('div');
        _liveRegion.setAttribute('role', 'status');
        _liveRegion.setAttribute('aria-live', 'polite');
        _liveRegion.setAttribute('aria-atomic', 'true');
        _liveRegion.className = 'intrivity-live-region';
        _liveRegion.style.cssText =
            'position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
            'overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
        document.body.appendChild(_liveRegion);
    }
    // Clearing first makes repeat announcements of the same text re-fire.
    _liveRegion.textContent = '';
    window.setTimeout(() => { _liveRegion.textContent = message; }, 50);
};

// Plain-language names for the MRTS numerals, so the shape graphics have a text
// equivalent instead of relying on shape alone (WCAG 1.1.1 / 1.4.1).
export const mrtsShapeName = (score) => (score >= 1 && score <= 5 ? t(`a11y.shape${score}`) : '');

// Text alternative for a domain score badge/icon.
export const describeScore = function(domainName, score, raw) {
    const shape = mrtsShapeName(score);
    const rawText = (raw === undefined || raw === null) ? '' : t('a11y.rawScore', { raw: Number(raw).toFixed(1) });
    return `${t('a11y.mrtsScore', { domain: domainName, score })}${rawText}${shape ? ` (${shape})` : ''}`;
};

// Escapes text destined for an XML/SVG attribute. The chart components build
// their SVG as a string and hand it to unsafeHTML, so anything interpolated
// into an attribute (labels pulled from the domain JSON) has to be escaped.
export const escapeXml = function(str) {
    return String(str === undefined || str === null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
};

// The four MRTS domain colours are light — they were chosen as fills, and all
// four fail WCAG AA when used as text on white (orange 2.0:1, green 1.7:1,
// teal 3.0:1, pink 3.7:1). These are darkened variants of the same hues that
// clear 4.5:1 on white, for use wherever the domain colour is applied to text.
// Fills, borders and backgrounds keep the original brand colours.
const DOMAIN_TEXT_COLORS = {
    'rgb(250, 166, 53)': 'rgb(166, 97, 0)',    // Message  — 4.9:1
    'rgb(82, 162, 170)': 'rgb(30, 120, 128)',  // Relationship — 5.2:1
    'rgb(175, 208, 117)': 'rgb(90, 120, 40)',  // Space — 5.1:1
    'rgb(217, 90, 123)': 'rgb(180, 40, 80)'    // Time — 6.3:1
};

export const accessibleTextColor = function(color) {
    if (!color) return color;
    const key = String(color).replace(/\s+/g, ' ').trim();
    return DOMAIN_TEXT_COLORS[key] || color;
};

// Near-black that reads on every one of the four domain colours used as a
// background (worst case is Time pink at 4.7:1). White text on those same
// backgrounds ranges from 1.7:1 to 3.7:1 and fails throughout.
export const ON_DOMAIN_TEXT = '#1a1a1a';
